/**
 * CandidateSignals — V2.0.0 (Feb 15, 2026)
 *
 * Superadmin-only Candidate Signals dashboard.
 * A heatmap grid of every candidate signal we track (verified / boosted /
 * online / origin / profile completion / pending actions) with a computed
 * 0-100 "Hero score" so the operator can spot the strongest and weakest
 * profiles at a glance.
 *
 * Signal cells are colour-graded (soft-grey → emerald / amber) so a "hero"
 * row lights up as a green bar and a "cold" row stays mostly grey. Filter
 * chips at the top drive both the KPI tiles and the grid. Row click opens
 * the candidate in a new tab (`/candidates` uses querystring-driven person
 * drawer already).
 *
 * Scales: virtualization not needed until candidate count exceeds ~500;
 * server caps scan at 5k rows per request and reports `truncated: true`
 * when the user needs to narrow filters further.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Broadcast,
    ArrowClockwise,
    MagnifyingGlass,
    SealCheck,
    Rocket,
    Circle,
    User,
    Warning,
    Sparkle,
    Snowflake,
    Fire,
    ChatCircleDots,
    Buildings,
    UserPlus,
    Lightning,
    FileText,
    CaretRight,
    IdentificationCard,
} from "@phosphor-icons/react";
import api from "@/lib/api";

// -------- Constants --------
const ORIGIN_META = {
    self_signup: { label: "Self-signup", color: "bg-emerald-500", icon: UserPlus },
    referral: { label: "Referral", color: "bg-emerald-400", icon: Sparkle },
    daddytalk: { label: "DaddyTalk", color: "bg-cobalt-500", icon: ChatCircleDots },
    fast_track: { label: "Fast-Track", color: "bg-cobalt-400", icon: Lightning },
    sourced: { label: "Sourced", color: "bg-amber-500", icon: Buildings },
};

const BUCKET_META = {
    hero: { label: "Hero", color: "text-emerald-700", bg: "bg-emerald-100", icon: Fire },
    ripening: { label: "Ripening", color: "text-amber-700", bg: "bg-amber-100", icon: Sparkle },
    cold: { label: "Cold", color: "text-ink/60", bg: "bg-ink/5", icon: Snowflake },
};

const CELL_BASE = "inline-flex items-center justify-center rounded-md text-[10px] font-bold uppercase tracking-wider h-6 min-w-[52px] px-2";
const HEAT = {
    on: "bg-emerald-500 text-white",
    warn: "bg-amber-400 text-white",
    off: "bg-ink/5 text-ink/40",
    active: "bg-cobalt-500 text-white",
};

const CHIP_BASE = "inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-bold uppercase tracking-wider transition-colors";

// -------- Presentational helpers --------
const KpiTile = ({ label, value, icon: Icon, tone, testid }) => (
    <div data-testid={testid} className={`rounded-xl border p-4 ${tone.border} ${tone.bg}`}>
        <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone.iconBg}`}>
                <Icon size={16} weight="fill" className={tone.iconColor} />
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${tone.label}`}>{label}</span>
        </div>
        <div className={`text-3xl font-black ${tone.value}`}>{value.toLocaleString("en-IN")}</div>
    </div>
);

const FilterChip = ({ active, onClick, children, testid, count }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`${CHIP_BASE} ${
            active
                ? "bg-emerald-500 text-white shadow-sm"
                : "bg-black/5 text-ink/70 hover:bg-black/10"
        }`}
    >
        {children}
        {count !== undefined && count !== null && (
            <span
                className={`inline-flex items-center justify-center rounded-full text-[10px] px-1.5 min-w-[18px] h-[18px] ${
                    active ? "bg-white/25 text-white" : "bg-ink/10 text-ink/70"
                }`}
            >
                {count}
            </span>
        )}
    </button>
);

const Cell = ({ on, label, tone = "on", testid }) => (
    <span data-testid={testid} className={`${CELL_BASE} ${on ? HEAT[tone] : HEAT.off}`}>{label}</span>
);

// Origin cell — coloured background per origin_label
const OriginCell = ({ label, testid }) => {
    const meta = ORIGIN_META[label] || { label: label || "—", color: "bg-ink/10", icon: User };
    const Icon = meta.icon;
    return (
        <span
            data-testid={testid}
            className={`${CELL_BASE} ${meta.color} text-white`}
            title={meta.label}
        >
            <Icon size={11} weight="bold" />
            <span className="ml-1 hidden md:inline">{meta.label}</span>
        </span>
    );
};

const CompletionBar = ({ pct, testid }) => {
    const tone =
        pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-400";
    return (
        <div data-testid={testid} className="flex items-center gap-2 w-[92px]">
            <div className="flex-1 h-1.5 rounded-full bg-ink/5 overflow-hidden">
                <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] font-bold tabular-nums text-ink/60 w-6 text-right">{pct}</span>
        </div>
    );
};

const ScoreBadge = ({ score, bucket, testid }) => {
    const meta = BUCKET_META[bucket];
    const Icon = meta.icon;
    return (
        <div
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-lg ${meta.bg} ${meta.color}`}
        >
            <Icon size={12} weight="fill" />
            <span className="text-sm font-black tabular-nums">{score}</span>
        </div>
    );
};

const PendingChip = ({ pending_verify, pending_cv, testid }) => {
    if (!pending_verify && !pending_cv) return <span className="text-[10px] text-ink/30">—</span>;
    return (
        <div data-testid={testid} className="flex flex-wrap gap-1">
            {pending_verify && (
                <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">
                    <SealCheck size={10} weight="bold" />
                    Verify
                </span>
            )}
            {pending_cv && (
                <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase">
                    <FileText size={10} weight="bold" />
                    No CV
                </span>
            )}
        </div>
    );
};

// -------- Main component --------
// V2.0.4 — friendly relative-time formatter for the timeline (never uses locale).
function _relTimeSignals(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso).getTime();
        const now = Date.now();
        const diff = Math.max(0, now - d);
        const min = Math.floor(diff / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min} min ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const day = Math.floor(hr / 24);
        if (day < 30) return `${day}d ago`;
        const mo = Math.floor(day / 30);
        return `${mo}mo ago`;
    } catch { return null; }
}

function TimelineDot({ tone, Icon, label, when, sub }) {
    const toneCls = {
        emerald: "bg-emerald-500",
        amber: "bg-amber-500",
        cobalt: "bg-cobalt-500",
        red: "bg-red-500",
        ink: "bg-ink/40",
    }[tone] || "bg-ink/40";
    return (
        <div className="flex items-start gap-3">
            <div className="relative flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white shadow-sm ${toneCls}`}>
                    <Icon size={13} weight="fill" />
                </div>
                <div className="w-px flex-1 bg-ink/10 my-0.5" />
            </div>
            <div className="flex-1 pb-3">
                <div className="text-[13px] font-bold text-ink">{label}</div>
                <div className="text-[11px] text-ink/50">{when || "—"}{sub ? ` · ${sub}` : ""}</div>
            </div>
        </div>
    );
}

function SignalTimeline({ row }) {
    // Build chronological events from the row's persisted timestamps.
    const events = [];
    if (row.created_at) events.push({ tone: "ink", Icon: UserPlus, label: "Candidate created", ts: row.created_at });
    if (row.verified_at) events.push({ tone: "emerald", Icon: SealCheck, label: "Verified badge approved", ts: row.verified_at, sub: "₹299 Paid" });
    if (row.kyc_done) events.push({ tone: "cobalt", Icon: IdentificationCard, label: `KYC verified${row.kyc_aadhar ? " (Aadhar)" : row.kyc_pan ? " (PAN)" : ""}`, ts: row.verified_at || row.updated_at });
    if (row.boost_expires_at) events.push({ tone: "amber", Icon: Lightning, label: "Boost active", ts: row.boost_expires_at, sub: `expires ${_relTimeSignals(row.boost_expires_at)}` });
    if (row.last_seen_at) events.push({ tone: row.online ? "emerald" : "ink", Icon: Circle, label: row.online ? "Online right now" : "Last seen", ts: row.last_seen_at });
    if (row.updated_at) events.push({ tone: "cobalt", Icon: Sparkle, label: "Profile last updated", ts: row.updated_at });
    if (row.pending_verify) events.push({ tone: "amber", Icon: Warning, label: "Verified-badge review pending", ts: row.updated_at });
    if (row.pending_cv) events.push({ tone: "red", Icon: FileText, label: "CV not uploaded yet", ts: null });

    // Sort DESC by timestamp (recent first). Null timestamps sink.
    events.sort((a, b) => {
        const at = a.ts ? new Date(a.ts).getTime() : 0;
        const bt = b.ts ? new Date(b.ts).getTime() : 0;
        return bt - at;
    });

    return (
        <div className="p-4 bg-emerald-50/30 border-l-4 border-emerald-400" data-testid={`signal-timeline-${row.id}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Signal Timeline · {row.name || row.email}</div>
                <Link to={`/candidates?open=${row.id}`} className="text-[10px] font-bold uppercase tracking-wider text-cobalt-600 hover:underline">Open profile →</Link>
            </div>
            {events.length === 0 ? (
                <div className="text-xs text-ink/50">No signal events yet.</div>
            ) : (
                <div className="max-w-md">
                    {events.map((e, i) => (
                        <TimelineDot key={i} tone={e.tone} Icon={e.Icon} label={e.label} when={_relTimeSignals(e.ts)} sub={e.sub} />
                    ))}
                </div>
            )}
        </div>
    );
}

// -------- Main component --------
export default function CandidateSignals() {
    const [stats, setStats] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloading, setReloading] = useState(false);

    // Filter state
    const [bucket, setBucket] = useState(null); // hero|ripening|cold|null(all)
    const [originFilter, setOriginFilter] = useState(null);
    const [flag, setFlag] = useState(null); // verified|boosted|online|pending_verify|pending_cv|null
    const [q, setQ] = useState("");
    const [recencyDays, setRecencyDays] = useState(7);
    const [expandedId, setExpandedId] = useState(null);

    const loadStats = useCallback(async (fresh = false) => {
        try {
            const params = new URLSearchParams({ recency_days: String(recencyDays) });
            if (fresh) params.set("fresh", "true");
            const r = await api.get(`/superadmin/candidate-signals/stats?${params.toString()}`);
            setStats(r.data);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || e?.message || "unknown";
            toast.error(`KPI stats failed (${status || "network"}): ${detail}`);
        }
    }, [recencyDays]);

    const loadRows = useCallback(async () => {
        setReloading(true);
        try {
            const params = new URLSearchParams({
                limit: "200",
                sort: "score",
                recency_days: String(recencyDays),
            });
            if (bucket) params.set("bucket", bucket);
            if (originFilter) params.set("origin", originFilter);
            if (q) params.set("q", q);
            if (flag === "verified") params.set("verified", "true");
            if (flag === "boosted") params.set("boosted", "true");
            if (flag === "online") params.set("online", "true");
            if (flag === "pending_verify") params.set("pending", "verify");
            if (flag === "pending_cv") params.set("pending", "cv");
            const r = await api.get(`/superadmin/candidate-signals?${params.toString()}`);
            setRows(r.data.rows || []);
            setTotal(r.data.total || 0);
            setTruncated(!!r.data.truncated);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || e?.message || "unknown";
            toast.error(`Signals load failed (${status || "network"}): ${detail}`);
        } finally {
            setReloading(false);
            setLoading(false);
        }
    }, [bucket, originFilter, q, flag, recencyDays]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadRows(); }, [loadRows]);

    const totals = stats?.totals || {};
    const activeFilters = useMemo(() => {
        const arr = [];
        if (bucket) arr.push(BUCKET_META[bucket]?.label);
        if (originFilter) arr.push(ORIGIN_META[originFilter]?.label);
        if (flag) arr.push(flag.replace("_", " "));
        return arr;
    }, [bucket, originFilter, flag]);

    return (
        <div className="p-4 md:p-6 space-y-5" data-testid="candidate-signals-page">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cobalt-500 to-emerald-500 flex items-center justify-center shadow-md">
                        <Broadcast size={20} weight="fill" className="text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl md:text-2xl font-black text-ink">Candidate Signals</h1>
                        <p className="text-xs text-ink/60 -mt-0.5">
                            Heatmap of every hero signal — organic, verified, boosted, online, agency, paid, completion, pending
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex items-center h-8 rounded-full bg-black/5 p-1 text-xs font-bold">
                        <button
                            data-testid="recency-7d-btn"
                            onClick={() => setRecencyDays(7)}
                            className={`px-3 h-6 rounded-full ${recencyDays === 7 ? "bg-white shadow text-ink" : "text-ink/60"}`}
                        >
                            7d
                        </button>
                        <button
                            data-testid="recency-30d-btn"
                            onClick={() => setRecencyDays(30)}
                            className={`px-3 h-6 rounded-full ${recencyDays === 30 ? "bg-white shadow text-ink" : "text-ink/60"}`}
                        >
                            30d
                        </button>
                    </div>
                    <button
                        data-testid="refresh-signals-btn"
                        onClick={() => { loadStats(true); loadRows(); }}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-black/5 hover:bg-black/10 text-xs font-bold text-ink/80 uppercase tracking-wider"
                    >
                        <ArrowClockwise size={14} weight="bold" className={reloading ? "animate-spin" : ""} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* KPI Tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-tiles">
                <KpiTile
                    testid="kpi-heroes"
                    label={`Heroes (${stats?.thresholds?.hero ?? 80}+)`}
                    value={totals.heroes || 0}
                    icon={Fire}
                    tone={{
                        border: "border-emerald-200", bg: "bg-emerald-50",
                        label: "text-emerald-700", value: "text-emerald-800",
                        iconBg: "bg-emerald-500", iconColor: "text-white",
                    }}
                />
                <KpiTile
                    testid="kpi-ripening"
                    label="Ripening (50-79)"
                    value={totals.ripening || 0}
                    icon={Sparkle}
                    tone={{
                        border: "border-amber-200", bg: "bg-amber-50",
                        label: "text-amber-700", value: "text-amber-800",
                        iconBg: "bg-amber-500", iconColor: "text-white",
                    }}
                />
                <KpiTile
                    testid="kpi-cold"
                    label="Cold (< 50)"
                    value={totals.cold || 0}
                    icon={Snowflake}
                    tone={{
                        border: "border-ink/10", bg: "bg-black/5",
                        label: "text-ink/60", value: "text-ink/80",
                        iconBg: "bg-ink/40", iconColor: "text-white",
                    }}
                />
                <KpiTile
                    testid="kpi-pending"
                    label="Pending Actions"
                    value={(totals.pending_verify || 0) + (totals.pending_cv || 0)}
                    icon={Warning}
                    tone={{
                        border: "border-red-200", bg: "bg-red-50",
                        label: "text-red-700", value: "text-red-800",
                        iconBg: "bg-red-500", iconColor: "text-white",
                    }}
                />
            </div>

            {/* Filter chips + search */}
            <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap" data-testid="filter-chips">
                    <FilterChip testid="chip-all" active={!bucket && !originFilter && !flag} onClick={() => { setBucket(null); setOriginFilter(null); setFlag(null); }}>All <span className="text-ink/50">{totals.total || 0}</span></FilterChip>
                    <FilterChip testid="chip-heroes" active={bucket === "hero"} onClick={() => setBucket(bucket === "hero" ? null : "hero")} count={totals.heroes}>Heroes</FilterChip>
                    <FilterChip testid="chip-verified" active={flag === "verified"} onClick={() => setFlag(flag === "verified" ? null : "verified")} count={totals.verified}>Verified</FilterChip>
                    <FilterChip testid="chip-online" active={flag === "online"} onClick={() => setFlag(flag === "online" ? null : "online")} count={totals.online}>Online</FilterChip>
                    <FilterChip testid="chip-boosted" active={flag === "boosted"} onClick={() => setFlag(flag === "boosted" ? null : "boosted")} count={totals.boosted}>Boosted</FilterChip>
                    <FilterChip testid="chip-pending-verify" active={flag === "pending_verify"} onClick={() => setFlag(flag === "pending_verify" ? null : "pending_verify")} count={totals.pending_verify}>Pending verify</FilterChip>
                    <FilterChip testid="chip-pending-cv" active={flag === "pending_cv"} onClick={() => setFlag(flag === "pending_cv" ? null : "pending_cv")} count={totals.pending_cv}>No CV</FilterChip>
                </div>
                <div className="flex items-center gap-2 flex-wrap" data-testid="origin-chips">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-ink/50 pl-1">Origin:</span>
                    {["self_signup", "referral", "daddytalk", "fast_track", "sourced"].map((k) => (
                        <FilterChip key={k} testid={`chip-origin-${k}`} active={originFilter === k} onClick={() => setOriginFilter(originFilter === k ? null : k)}>
                            {ORIGIN_META[k].label}
                        </FilterChip>
                    ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                        <input
                            data-testid="signals-search-input"
                            type="text"
                            placeholder="Search name or email…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full h-9 pl-9 pr-3 rounded-full bg-white border border-ink/10 focus:border-emerald-400 focus:outline-none text-sm"
                        />
                    </div>
                    <div className="text-[11px] text-ink/50">
                        Showing <b data-testid="signals-row-count">{rows.length}</b> of {total.toLocaleString("en-IN")}
                        {truncated && <span className="ml-2 text-amber-600 font-bold">· scan capped — narrow filters</span>}
                    </div>
                </div>
            </div>

            {/* Heatmap grid */}
            <div className="rounded-xl border border-ink/10 overflow-hidden bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full" data-testid="signals-heatmap">
                        <thead className="bg-black/5 text-[10px] font-bold uppercase tracking-wider text-ink/60">
                            <tr>
                                <th className="text-left px-3 py-2 sticky left-0 bg-black/5 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">Candidate</th>
                                <th className="text-left px-2 py-2">Score</th>
                                <th className="text-left px-2 py-2">Origin</th>
                                <th className="text-left px-2 py-2">Verified</th>
                                <th className="text-left px-2 py-2">KYC</th>
                                <th className="text-left px-2 py-2">Boost</th>
                                <th className="text-left px-2 py-2">Online</th>
                                <th className="text-left px-2 py-2">Recent</th>
                                <th className="text-left px-2 py-2">Profile</th>
                                <th className="text-left px-2 py-2">Pending</th>
                                <th className="text-left px-2 py-2 w-8"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="signals-tbody">
                            {loading && (
                                <tr><td colSpan={11} className="text-center py-8 text-ink/40 text-sm">Loading…</td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={11} className="text-center py-10 text-ink/40 text-sm">No candidates match the current filters.</td></tr>
                            )}
                            {!loading && rows.map((r) => {
                                const expanded = expandedId === r.id;
                                return (
                                <React.Fragment key={r.id}>
                                <tr data-testid={`signals-row-${r.id}`} className="border-t border-ink/5 hover:bg-emerald-50/30">
                                    <td className="px-3 py-2 sticky left-0 bg-white z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">
                                        <Link
                                            to={`/candidates?open=${r.id}`}
                                            className="flex items-center gap-2 group"
                                            data-testid={`signals-row-link-${r.id}`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cobalt-500 to-emerald-500 flex items-center justify-center text-white text-sm font-black shrink-0">
                                                {(r.name || r.email || "?").slice(0, 1).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-bold text-ink truncate group-hover:text-emerald-600 max-w-[220px]">{r.name || r.email}</div>
                                                <div className="text-[11px] text-ink/50 truncate max-w-[220px]">{r.current_title || "—"}{r.location ? ` · ${r.location}` : ""}</div>
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="px-2 py-2"><ScoreBadge score={r.score} bucket={r.bucket} testid={`cell-score-${r.id}`} /></td>
                                    <td className="px-2 py-2"><OriginCell label={r.origin_label} testid={`cell-origin-${r.id}`} /></td>
                                    <td className="px-2 py-2"><Cell on={r.verified} label={r.verified ? "Paid" : "No"} testid={`cell-verified-${r.id}`} /></td>
                                    <td className="px-2 py-2">
                                        <Cell
                                            on={r.kyc_done}
                                            label={r.kyc_done ? (r.kyc_aadhar ? "Aadhar" : r.kyc_pan ? "PAN" : "Done") : "No"}
                                            tone="active"
                                            testid={`cell-kyc-${r.id}`}
                                        />
                                    </td>
                                    <td className="px-2 py-2"><Cell on={r.boosted} label={r.boosted ? "Yes" : "No"} tone="warn" testid={`cell-boosted-${r.id}`} /></td>
                                    <td className="px-2 py-2"><Cell on={r.online} label={r.online ? "On" : "Off"} testid={`cell-online-${r.id}`} /></td>
                                    <td className="px-2 py-2"><Cell on={r.recent} label={r.recent ? "Yes" : "Old"} tone="active" testid={`cell-recent-${r.id}`} /></td>
                                    <td className="px-2 py-2"><CompletionBar pct={r.completion} testid={`cell-completion-${r.id}`} /></td>
                                    <td className="px-2 py-2"><PendingChip pending_verify={r.pending_verify} pending_cv={r.pending_cv} testid={`cell-pending-${r.id}`} /></td>
                                    <td className="px-2 py-2 w-8">
                                        <button
                                            data-testid={`signals-row-expand-${r.id}`}
                                            onClick={() => setExpandedId(expanded ? null : r.id)}
                                            className="w-7 h-7 rounded-lg hover:bg-black/5 inline-flex items-center justify-center text-ink/60"
                                            aria-label={expanded ? "Collapse timeline" : "Expand timeline"}
                                        >
                                            <CaretRight
                                                size={14}
                                                weight="bold"
                                                className={`transition-transform ${expanded ? "rotate-90" : ""}`}
                                            />
                                        </button>
                                    </td>
                                </tr>
                                {expanded && (
                                    <tr data-testid={`signals-row-expanded-${r.id}`}>
                                        <td colSpan={11} className="p-0 bg-white">
                                            <SignalTimeline row={r} />
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-[11px] text-ink/40 text-center">
                Hero score = verified (+25) · completion≥80 (+20) · boosted (+15) · online (+10) · high-intent origin (+10) · recent activity (+10) · has basics (+10)
                {stats?.computed_at && (
                    <span className="ml-2">· KPI cache 60 s</span>
                )}
            </p>
        </div>
    );
}
